// src/pages/AccountDetail.js

import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Avatar,
  IconButton,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  AttachMoney,
  Group,
  Storage,
  Shield,
  MedicalServices
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Papa from 'papaparse';
import Plot from 'react-plotly.js';

import { AuthContext } from '../context/AuthContext';
import loadingGif from '../assets/autopoint-loading-svg.gif';

// Helper: load & parse CSV as a promise
function parseCSV(url) {
  return new Promise((resolve, reject) => {
    Papa.parse(url, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => resolve(data),
      error: err => reject(err)
    });
  });
}

// TinyDonut for a compact percentage visualization
function TinyDonut({ percentage = 0, size = 16, strokeWidth = 2, color = '#6c5ce7' }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (circumference * percentage) / 100;
  return (
    <svg width={size} height={size}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill='none'
        stroke='#e0e0e0'
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill='none'
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap='round'
        style={{ transition: 'stroke-dashoffset 0.4s ease' }}
      />
    </svg>
  );
}

export default function AccountDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  // scroll to top on account change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // derive partner filter
  const domain = user?.email.split('@')[1]?.toLowerCase() || '';
  const partnerFilter =
    domain === 'merchantsbonding.com' ? 'merchants' :
    domain === 'colonialsurety.com' ? 'colonial' :
    domain === 'everestglobal.com' ? 'everest' : '';

  // component state
  const [subsidiaryData, setSubsidiaryData] = useState([]);
  const [selectedYear, setSelectedYear] = useState('2025');
  const [loadingTab, setLoadingTab] = useState(true);
  const [loadingYearData, setLoadingYearData] = useState(false);
  const [yearStats, setYearStats] = useState({ accountStats: [], sharedSavingsMap: {}, clinicianCounts: {} });
  const [expandedRow, setExpandedRow] = useState(null);
  const fetchIdRef = useRef(0);

  // normalize helper
  const normalize = s => String(s || '').toLowerCase().trim();
  // parse percent number
  const parseOneDecimal = v => {
    const n = parseFloat(String(v).replace(/[$,%]/g, '').trim());
    return isNaN(n) ? 0 : parseFloat(n.toFixed(1));
  };

  // load subsidiary list
  useEffect(() => {
    parseCSV('/data/ACO_subsidiary list.csv')
      .then(data => setSubsidiaryData(data))
      .catch(() => setSubsidiaryData([]));
  }, [partnerFilter, id]);

  // derive parent info and available years
  const { parentName, parentStopLoss, availableYears, subsidiaries } = useMemo(() => {
    if (!subsidiaryData.length) return { parentName: '', parentStopLoss: '', availableYears: [], subsidiaries: [] };
    const parentRow = subsidiaryData.find(r => normalize(r.ACO_ID) === normalize(id));
    if (!parentRow) return { parentName: '', parentStopLoss: '', availableYears: [], subsidiaries: [] };
    const pName = parentRow.parent_co;
    const pStop = parentRow.stop_loss || '';
    const yearsSet = new Set();
    subsidiaryData.forEach(r => {
      if (r.parent_co === pName && r['Performance Years']) {
        r['Performance Years'].split(',').map(y => y.trim()).forEach(y => yearsSet.add(y));
      }
    });
    const yearsArr = Array.from(yearsSet).sort();
    const subs = subsidiaryData
      .filter(r =>
        r.parent_co === pName &&
        normalize(r.surety_access) === partnerFilter &&
        r['Performance Years'].split(',').map(y => y.trim()).includes(selectedYear)
      )
      .map(r => ({ id: r.ACO_ID.trim(), name: r.subsidiary_co.trim(), program: r.program_type.trim() }));
    return { parentName: pName, parentStopLoss: pStop, availableYears: yearsArr, subsidiaries: subs };
  }, [subsidiaryData, id, partnerFilter, selectedYear]);

  // fetch year-specific data
  useEffect(() => {
    if (!subsidiaries.length) return;
    setLoadingYearData(true);
    const fetchId = ++fetchIdRef.current;
    (async () => {
      try {
        const savCsv = selectedYear === '2024' ? '/data/2024_data.csv' : '/data/shared_savings.csv';
        const clinCsv = selectedYear === '2024' ? '/data/2024_data.csv' : '/data/npi_output.csv';
        const [savData, clinData] = await Promise.all([
          parseCSV(savCsv),
          parseCSV(clinCsv)
        ]);
        let totSav = 0, totMem = 0;
        const sharedSavingsMap = {};
        const clinicianCounts = {};
        const quarters = ['Q1','Q2','Q3','Q4'];
        subsidiaries.forEach(sub => {
          const row = savData.find(r => normalize(r.ID) === normalize(sub.id));
          const gross = parseFloat(row?.Savings_Gross) || 0;
          const mem = parseFloat(row?.Membership) || 0;
          totSav += gross;
          totMem += mem;
          const qKey = selectedYear === '2024' ? 'Q4' : 'Q2';
          sub.projectedSavings = row && row[qKey] ? `${parseOneDecimal(row[qKey])}%` : '';
          sub.score = row?.sparx || '';
          const lbKey = selectedYear === '2024' ? '2023 Lookback Savings' : '2024 Lookback Savings';
          const base = parseFloat(row?.[lbKey]) || null;
          const curr = quarters.map(q => parseFloat(row?.[q]) || null);
          const ci = quarters.map(q => parseFloat(row?.[`${q} CI`]) || null);
          sharedSavingsMap[sub.id] = [
            { x: quarters, y: quarters.map(() => base), mode: 'lines+markers', name: `${selectedYear - 1}`, marker: { color: 'gray', size: 8 }, line: { color: 'gray' } },
            { x: quarters, y: curr, mode: 'lines+markers', name: selectedYear, marker: { color: '#6c5ce7', size: 8 }, line: { color: '#6c5ce7' }, error_y: { type: 'data', array: ci } }
          ];
          if (selectedYear === '2024') {
            const rr = clinData.find(r => normalize(r.ID) === normalize(sub.id));
            clinicianCounts[sub.id] = parseInt(rr?.physicians, 10) || 0;
          } else {
            clinicianCounts[sub.id] = clinData.filter(r => normalize(r['Entity ID']) === normalize(sub.id)).length;
          }
        });
        const accountStats = [
          { title: 'Generated Savings', value: `$${Math.round(totSav).toLocaleString()}`, icon: <AttachMoney /> },
          { title: 'Membership', value: totMem.toLocaleString(), icon: <Group /> },
          { title: 'Bonds', value: subsidiaries.length.toString(), icon: <Storage /> }
        ];
        if (fetchId === fetchIdRef.current) {
          setYearStats({ accountStats, sharedSavingsMap, clinicianCounts });
          setLoadingYearData(false);
          setLoadingTab(false);
        }
      } catch {
        if (fetchId === fetchIdRef.current) {
          setLoadingYearData(false);
          setLoadingTab(false);
        }
      }
    })();
  }, [selectedYear, subsidiaries]);

  // chart layout
  const sharedSavingsLayout = useMemo(() => ({
    title: 'Projected % Savings',
    xaxis: { title: 'Quarter' },
    yaxis: { title: 'Percentage %', rangemode: 'tozero', ticksuffix: '%' },
    margin: { t: 40, r: 20, l: 50, b: 40 },
    autosize: true,
    hovermode: 'closest',
  }), []);

  // initial loading
  if (loadingTab) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <img src={loadingGif} alt='Loading…' width={80} height={80} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/accounts')} sx={{ mr: 1 }}><ArrowBackIcon /></IconButton>
        <Typography variant='h5' fontWeight='bold'>{parentName}</Typography>
      </Box>

      {/* Year Selector */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        {availableYears.map(year => (
          <Button
            key={year}
            variant={selectedYear === year ? 'contained' : 'outlined'}
            onClick={() => setSelectedYear(year)}
          >
            {year}
          </Button>
        ))}
      </Box>

      {/* Stats Cards */}
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, opacity: loadingYearData ? 0.5 : 1 }}>
          {yearStats.accountStats.map((stat, idx) => (
            <Box key={idx} sx={{ flex: '1 1 250px' }}>
              <Card sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: '#6c5ce7', mr: 2 }}>{stat.icon}</Avatar>
                  <Box>
                    <Typography variant='subtitle2' color='textSecondary'>{stat.title}</Typography>
                    <Typography variant='h6' fontWeight='bold'>{stat.value}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
        {loadingYearData && (
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}
      </Box>

      {/* Subsidiaries Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ACO Name</TableCell>
              <TableCell>Program</TableCell>
              <TableCell>Projected Savings</TableCell>
              <TableCell>Sparx Score</TableCell>
              <TableCell align='right'>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subsidiaries.map(sub => (
              <React.Fragment key={sub.id}>
                <TableRow hover onClick={() => setExpandedRow(prev => (prev === sub.id ? null : sub.id))} sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {expandedRow === sub.id ? <KeyboardArrowDownIcon sx={{ mr: 1 }} /> : <KeyboardArrowRightIcon sx={{ mr: 1 }} />}
                      {sub.name}
                    </Box>
                  </TableCell>
                  <TableCell>{sub.program}</TableCell>
                  <TableCell>{sub.projectedSavings || 'N/A'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant='body2' sx={{ mr: 1 }}>{sub.score || 'N/A'}</Typography>
                      <TinyDonut percentage={Number(sub.score)} size={16} strokeWidth={2} />
                    </Box>
                  </TableCell>
                  <TableCell align='right'>
                    <Button size='small' variant='contained' onClick={e => { e.stopPropagation(); navigate(`/accounts/${sub.id}/report/${selectedYear}`); }} sx={{ backgroundColor: '#6c5ce7', '&:hover': { backgroundColor: '#5346d9' } }}>
                      View Report
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expandedRow === sub.id} timeout='auto' unmountOnExit>
                      <Box sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          {yearStats.sharedSavingsMap[sub.id] && (
                            <Box sx={{ flex: 2, position: 'relative' }}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant='subtitle1' fontWeight='bold' sx={{ mb: 1 }}>
                                  Shared Savings Chart
                                </Typography>
                                <Box sx={{ height: 300, opacity: loadingYearData ? 0.5 : 1 }}>
                                  <Plot data={yearStats.sharedSavingsMap[sub.id]} layout={sharedSavingsLayout} useResizeHandler style={{ width: '100%', height: '100%' }} />
                                </Box>
                                {loadingYearData && (
                                  <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <CircularProgress size={48} />
                                  </Box>
                                )}
                              </Paper>
                            </Box>
                          )}
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Card sx={{ flex: 1 }}>
                              <CardContent sx={{ textAlign: 'center' }}>
                                <Shield sx={{ fontSize: 40, color: '#6c5ce7', mb: 1 }} />
                                <Typography variant='subtitle2' fontWeight='bold'>Stop Loss</Typography>
                                <Typography variant='body2' color='textSecondary'>{parentStopLoss || 'N/A'}</Typography>
                              </CardContent>
                            </Card>
                            <Card sx={{ flex: 1 }}>
                              <CardContent sx={{ textAlign: 'center' }}>
                                <MedicalServices sx={{ fontSize: 40, color: '#6c5ce7', mb: 1 }} />
                                <Typography variant='subtitle2' fontWeight='bold'>Physicians</Typography>
                                <Typography variant='body2' color='textSecondary'>{yearStats.clinicianCounts[sub.id] ?? 'N/A'}</Typography>
                              </CardContent>
                            </Card>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
